import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Users, Award } from "lucide-react";
import { Helmet } from "@/components/Helmet";

const About = () => {
  const values = [
    { icon: Shield, title: "Security First", description: "Risk reduction and human validation are our priorities" },
    { icon: Users, title: "Client Focused", description: "We help you understand risk and act with confidence" },
    { icon: Award, title: "Expert Team", description: "Years of WordPress security experience" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>About ShadowScan | WordPress Security Team</title>
        <meta
          name="description"
          content="Meet ShadowScan's Australian team behind free ShadowScan Guard, Basic Hosting, and monitored WordPress security plans."
        />
        <link rel="canonical" href="https://shadowscan.com.au/about" />
        <meta property="og:title" content="About ShadowScan | WordPress Security Team" />
        <meta property="og:description" content="Meet ShadowScan's Australian team behind free ShadowScan Guard, Basic Hosting, and monitored WordPress security plans." />
        <meta property="og:url" content="https://shadowscan.com.au/about" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "ShadowScan",
          url: "https://shadowscan.com.au/",
          logo: "https://shadowscan.com.au/favicon.png",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: "support@shadowscan.com.au",
            areaServed: "AU",
            availableLanguage: ["English"]
          }
        })}</script>
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// ABOUT_US</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">Meet <span className="text-gradient">ShadowScan</span></h1>
              <p className="text-lg text-muted-foreground leading-relaxed">WordPress security experts who help businesses reduce risk with managed oversight</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto space-y-6 text-muted-foreground leading-relaxed">
              <p>ShadowScan is run by WordPress security experts who are passionate about reducing risk for businesses online. We understand that not everyone is a tech expert, and that's exactly why we're here.</p>
              <p>Our mission is simple: make WordPress security accessible and understandable, from free ShadowScan Guard through Basic Hosting and higher-touch monitored plans. We take complex security concepts and break them down into clear, actionable steps.</p>
              <p>When you work with ShadowScan, the support level depends on the service you choose. Free Guard surfaces protection and guidance, Basic Hosting covers hosted onboarding, and monitored plans add validation, coordination, and higher-touch help where included.</p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// OUR_VALUES</div>
              <h2 className="text-3xl font-heading font-bold">What Drives Us</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card key={index} className="cyber-card text-center hover:border-primary/50 transition-all">
                    <CardContent className="pt-8 pb-6">
                      <div className="w-14 h-14 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-7 w-7 text-primary" />
                      </div>
                      <h3 className="font-heading font-semibold mb-2">{value.title}</h3>
                      <p className="text-sm text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
